import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, Target, Eye, Handshake, TrendingUp,
  Building2, Database, Cloud, Brain, Users
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function AboutPage() {
  const differentiators = [
    {
      icon: Building2,
      title: 'Engineering studio, not a generalist consultancy',
      description: 'We focus deeply on data, cloud and AI engineering. No distractions, no generalist advice – just hands-on delivery.'
    },
    {
      icon: Database,
      title: 'Data, cloud and AI under one roof',
      description: 'Modern platforms need all three. We bring integrated expertise instead of siloed specialists.'
    },
    {
      icon: Users,
      title: 'Pods model aligned to how modern teams ship',
      description: 'Our talent model matches how engineering teams actually work – agile, integrated, outcome-focused.'
    },
    {
      icon: Handshake,
      title: 'Rooted in both technology and talent',
      description: 'We understand both sides – building great platforms and building great teams to run them.'
    }
  ];

  const values = [
    { icon: Target, title: 'Outcome-first', description: 'We measure success by what ships and the value it delivers.' },
    { icon: Eye, title: 'Transparent engagement', description: 'Clear pricing, honest communication, no hidden surprises.' },
    { icon: Handshake, title: 'Long-term partnerships', description: 'We aim to be your trusted engineering partner, not a one-time vendor.' },
    { icon: TrendingUp, title: 'Continuous improvement', description: 'We stay current with modern practices and bring those learnings to every engagement.' }
  ];

  const services = [
    { icon: Database, name: 'Data' },
    { icon: Cloud, name: 'Cloud' },
    { icon: Brain, name: 'AI Services' },
    { icon: Users, name: 'Engineering Pods' }
  ];

  return (
    <div data-testid="about-page">
      <Helmet>
        <title>About - Cynergy Services</title>
        <meta name="description" content="Cynergy is a boutique engineering studio focused on data, cloud, AI and the teams that deliver them. Based in India and Singapore." />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-white to-accent/5" data-testid="about-hero">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div initial="initial" animate="animate" variants={fadeInUp}>
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">About Us</p>
              <h1 className="heading-1 text-foreground mb-4">About Cynergy Services</h1>
              <p className="body-lg">
                A boutique engineering studio focused on data, cloud, AI and the teams that deliver them.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/jipc0tyd_About%20Us.jpeg"
                alt="Professional team working"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white" data-testid="about-story">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Our Story</p>
              <h2 className="heading-2 text-foreground mb-4">Why Cynergy exists</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Cynergy was founded with a clear purpose: to bridge the gap between strategic vision 
                  and hands-on engineering delivery. Too many organizations struggle to turn their data, 
                  cloud and AI ambitions into reality.
                </p>
                <p>
                  Our founding team brings together deep expertise in digital transformation, technology 
                  consulting, and talent solutions. We've seen firsthand how the right combination of 
                  engineering capability and team model can accelerate outcomes dramatically.
                </p>
                <p>
                  Based in India and Singapore and serving clients across APAC and globally, we operate as a 
                  focused studio – not a generalist consultancy.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-secondary/50 rounded-sm p-6">
                <h3 className="font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Our four offerings
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {services.map((service, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                        <service.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">{service.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="section-padding bg-secondary/50" data-testid="about-differentiators">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Our Approach</p>
            <h2 className="heading-2 text-foreground">What makes us different</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-border/60" data-testid={`differentiator-${index}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground text-sm mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {item.title}
                        </h3>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-foreground text-white" data-testid="about-values">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Our Values</p>
            <h2 className="heading-2">What we stand for</h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`value-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center mb-3 mx-auto">
                  <value.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="text-xs text-white/70">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary" data-testid="about-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-2 text-white mb-4">Want to know if we're the right fit?</h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Let's have a conversation about your challenges and see if there's a good match.
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90" data-testid="about-final-cta">
                Talk to us
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
